/**
 * Autosave for the call card.
 *
 * A call-taker types while an emergency is happening. Nothing they enter should
 * depend on them remembering to press Save, and nothing should be lost if the
 * browser dies or the machine reboots mid-call.
 *
 * Rules this enforces, each of which exists because the naive version gets it
 * wrong during a real call:
 *
 *  - Debounced, so it saves on a pause in typing rather than per keystroke.
 *  - ONE request in flight at a time. Two overlapping PATCHes can land out of
 *    order and the older one wins, silently reverting a correction.
 *  - Anything typed while a save is in flight is queued and sent straight after,
 *    so the last thing typed is always what ends up stored.
 *  - Failures are surfaced, never swallowed. A call-taker must be able to see
 *    that the address did not save.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

interface Options<T> {
  /** Milliseconds of quiet before a save fires. */
  delay?: number;
  onSave: (patch: T) => Promise<unknown>;
}

export function useAutosave<T extends Record<string, unknown>>({
  delay = 800,
  onSave,
}: Options<T>) {
  const [state, setState] = useState<SaveState>('idle');
  const [error, setError] = useState<string | null>(null);

  const pending = useRef<Record<string, unknown>>({});
  const inFlight = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const flush = useCallback(async () => {
    if (inFlight.current) return; // the queue drains when the current save lands
    const patch = pending.current;
    if (Object.keys(patch).length === 0) return;

    pending.current = {};
    inFlight.current = true;
    if (mounted.current) setState('saving');

    try {
      await onSave(patch as T);
      if (mounted.current) {
        setState('saved');
        setError(null);
      }
    } catch (e) {
      // Put it back so the next attempt still carries it. Merged UNDER anything
      // typed since, which is newer and must win.
      pending.current = { ...patch, ...pending.current };
      if (mounted.current) {
        setState('error');
        setError(e instanceof Error ? e.message : 'Could not save');
      }
    } finally {
      inFlight.current = false;
      // Something was typed while that request was in the air.
      if (Object.keys(pending.current).length > 0) void flush();
    }
  }, [onSave]);

  /** Queue a change. Nested objects merge one level, matching the API. */
  const save = useCallback(
    (patch: Record<string, unknown>) => {
      for (const [key, value] of Object.entries(patch)) {
        const existing = pending.current[key];
        const bothObjects =
          typeof existing === 'object' &&
          existing !== null &&
          !Array.isArray(existing) &&
          typeof value === 'object' &&
          value !== null &&
          !Array.isArray(value);
        pending.current[key] = bothObjects ? { ...existing, ...value } : value;
      }

      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => void flush(), delay);
    },
    [delay, flush],
  );

  /** Save immediately — used on blur and before navigating away. */
  const saveNow = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    return flush();
  }, [flush]);

  return { save, saveNow, state, error, hasPending: () => Object.keys(pending.current).length > 0 };
}
